import { useState, useEffect, useRef, useCallback } from 'react';
import { trNorm } from '@/lib/arama';

const API_BASE = window.location.origin + '/api/erp';

export default function BarcodeAssign() {
  const [token, setToken] = useState(localStorage.getItem('erp_token'));
  const [company, setCompany] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [subcatFilter, setSubcatFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [scanning, setScanning] = useState(null);
  const [scannedCode, setScannedCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [filterIds, setFilterIds] = useState(null); // IDs from URL param
  const videoRef = useRef(null);
  const readerRef = useRef(null);

  // Read IDs from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ids = params.get('ids');
    if (ids) setFilterIds(ids.split(',').filter(Boolean));
  }, []);

  // Auto-login if token exists
  useEffect(() => {
    if (token) {
      fetch(`${API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => { if (d.company) setCompany(d.company); })
        .catch(() => setToken(null));
    }
  }, [token]);

  // Load products and categories
  useEffect(() => {
    if (!token || !company) return;
    const params = new URLSearchParams({ per_page: '5000' });
    fetch(`${API_BASE}/products?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setProducts(d.items || d))
      .catch(() => {});
    fetch(`${API_BASE}/categories`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setCategories(d))
      .catch(() => {});
  }, [token, company]);

  const login = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      const r = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: fd.get('email'), password: fd.get('password') })
      });
      const d = await r.json();
      if (d.token) {
        localStorage.setItem('erp_token', d.token);
        setToken(d.token);
        if (d.companies?.length === 1) {
          const r2 = await fetch(`${API_BASE}/auth/select-company`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${d.token}` },
            body: JSON.stringify({ company_id: d.companies[0].id })
          });
          const d2 = await r2.json();
          localStorage.setItem('erp_token', d2.token);
          setToken(d2.token);
          setCompany(d2.company);
        }
      }
    } catch {}
  };

  const startScan = async (productId) => {
    setScanning(productId);
    setScannedCode('');
    try {
      const { BrowserMultiFormatReader } = await import('@zxing/browser');
      const reader = new BrowserMultiFormatReader();
      readerRef.current = reader;
      setTimeout(async () => {
        if (!videoRef.current) return;
        try {
          await reader.decodeFromVideoDevice(undefined, videoRef.current, (result) => {
            if (result) {
              const code = result.getText();
              setScannedCode(code);
              if (navigator.vibrate) navigator.vibrate(100);
              stopScan();
            }
          });
        } catch (err) { console.error(err); }
      }, 200);
    } catch {}
  };

  const stopScan = () => {
    readerRef.current = null;
    // Don't reset scanning - keep it to show confirm UI
  };

  const confirmBarcode = async () => {
    if (!scanning || !scannedCode) return;
    setLoading(true);
    try {
      await fetch(`${API_BASE}/products/${scanning}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ barcode: scannedCode })
      });
      setProducts(prev => prev.map(p => p.id === scanning ? { ...p, barcode: scannedCode } : p));
      setScanning(null);
      setScannedCode('');
    } catch {}
    setLoading(false);
  };

  const cancelScan = () => { setScanning(null); setScannedCode(''); readerRef.current = null; };

  const parentCats = categories.filter(c => !c.parent_id).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
  const subcats = categories.filter(c => c.parent_id === catFilter).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));

  const filtered = products.filter(p => {
    if (filterIds && !filterIds.includes(p.id)) return false;
    if (catFilter && p.category_id !== catFilter) return false;
    if (subcatFilter && p.subcategory_id !== subcatFilter) return false;
    if (typeFilter && p.type !== typeFilter) return false;
    if (search) {
      const s = trNorm(search);
      if (!trNorm(p.name).includes(s) && !trNorm(p.code).includes(s) && !(p.barcode || '').includes(s)) return false;
    }
    return true;
  });

  // Login screen
  if (!token || !company) {
    return (
      <div style={styles.container}>
        <div style={styles.loginCard}>
          <h1 style={styles.title}>Barkod Tanimlama</h1>
          <form onSubmit={login} style={styles.form}>
            <input name="email" type="email" placeholder="E-posta" style={styles.input} />
            <input name="password" type="password" placeholder="Sifre" style={styles.input} />
            <button type="submit" style={styles.btnPrimary}>Giris Yap</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Barkod Tanimlama</h1>
        <p style={styles.subtitle}>
          {filtered.length} urun
          {filterIds && <span style={{ color: '#3d6e4e', marginLeft: 8 }}>({filterIds.length} secili)</span>}
        </p>
        {filterIds && (
          <button onClick={() => setFilterIds(null)} style={styles.showAllBtn}>
            Tum urunleri goster
          </button>
        )}
      </div>

      {/* Search */}
      <input
        type="text" value={search} onChange={(e) => setSearch(e.target.value)}
        placeholder="Urun ara (kod, ad veya barkod)..."
        style={styles.searchInput}
      />

      {/* Filters */}
      <div style={styles.filterRow}>
        <select value={catFilter} onChange={(e) => { setCatFilter(e.target.value); setSubcatFilter(''); }} style={styles.filterSelect}>
          <option value="">Tum Kategoriler</option>
          {parentCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={subcatFilter} onChange={(e) => setSubcatFilter(e.target.value)} style={styles.filterSelect} disabled={!catFilter}>
          <option value="">Tum Alt Kat.</option>
          {subcats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={styles.filterSelect}>
          <option value="">Tum Turler</option>
          <option value="raw_material">Hammadde</option>
          <option value="semi_finished">Yari Mamul</option>
          <option value="finished">Mamul</option>
          <option value="packaging">Ambalaj</option>
          <option value="other">Diger</option>
        </select>
      </div>

      {/* Scanner Modal */}
      {scanning && (
        <div style={styles.overlay}>
          <div style={styles.scanCard}>
            <h3 style={styles.scanTitle}>
              {products.find(p => p.id === scanning)?.name}
            </h3>
            {!scannedCode ? (
              <>
                <div style={styles.videoContainer}>
                  <video ref={videoRef} style={styles.video} />
                  <div style={styles.scanFrame} />
                </div>
                <p style={styles.scanHint}>Barkodu kameraya gosterin</p>
                <button onClick={cancelScan} style={styles.btnCancel}>Iptal</button>
              </>
            ) : (
              <>
                <div style={styles.barcodeResult}>
                  <p style={styles.barcodeLabel}>Okunan Barkod:</p>
                  <p style={styles.barcodeValue}>{scannedCode}</p>
                </div>
                <div style={styles.btnRow}>
                  <button onClick={cancelScan} style={styles.btnCancel}>Iptal</button>
                  <button onClick={confirmBarcode} disabled={loading} style={styles.btnConfirm}>
                    {loading ? 'Kaydediliyor...' : 'Onayla ve Kaydet'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Product List */}
      <div style={styles.list}>
        {filtered.map(p => (
          <div key={p.id} style={styles.productRow}>
            <div style={styles.productInfo}>
              <span style={styles.productCode}>{p.code}</span>
              <span style={styles.productName}>{p.name}</span>
              {p.barcode && <span style={styles.barcodeBadge}>{p.barcode}</span>}
            </div>
            <button
              onClick={() => startScan(p.id)}
              style={p.barcode ? styles.btnRescan : styles.btnScan}
            >
              {p.barcode ? 'Degistir' : 'Tara'}
            </button>
          </div>
        ))}
        {filtered.length === 0 && <p style={styles.empty}>Urun bulunamadi</p>}
      </div>
    </div>
  );
}

const styles = {
  container: { maxWidth: 600, margin: '0 auto', padding: 16, fontFamily: 'Manrope, -apple-system, sans-serif', minHeight: '100vh', background: '#f5f0e8' },
  header: { marginBottom: 16 },
  title: { fontSize: 24, fontWeight: 800, color: '#1a1916', margin: 0 },
  subtitle: { fontSize: 13, color: '#8a7e70', margin: '4px 0 0' },
  showAllBtn: { marginTop: 6, padding: '4px 10px', fontSize: 12, color: '#3d6e4e', background: '#3d6e4e15', border: '1px solid #3d6e4e30', borderRadius: 6, cursor: 'pointer' },
  loginCard: { background: '#faf7f2', border: '1px solid #d0c4b0', borderRadius: 12, padding: 32, marginTop: 80 },
  form: { display: 'flex', flexDirection: 'column', gap: 12, marginTop: 20 },
  input: { padding: '12px 16px', border: '1px solid #d0c4b0', borderRadius: 8, fontSize: 16, background: '#fff', outline: 'none' },
  searchInput: { width: '100%', padding: '14px 16px', border: '1px solid #d0c4b0', borderRadius: 10, fontSize: 16, background: '#fff', outline: 'none', marginBottom: 8, boxSizing: 'border-box' },
  filterRow: { display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' },
  filterSelect: { flex: 1, minWidth: 100, padding: '8px 10px', border: '1px solid #d0c4b0', borderRadius: 8, fontSize: 13, background: '#fff', color: '#1a1916', outline: 'none', appearance: 'auto' },
  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  productRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#faf7f2', border: '1px solid #d0c4b0', borderRadius: 10 },
  productInfo: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1, marginRight: 12 },
  productCode: { fontSize: 11, fontFamily: 'monospace', color: '#8a7e70' },
  productName: { fontSize: 14, fontWeight: 600, color: '#1a1916', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  barcodeBadge: { fontSize: 11, color: '#3d6e4e', fontFamily: 'monospace', background: '#3d6e4e15', padding: '2px 6px', borderRadius: 4, width: 'fit-content' },
  btnScan: { padding: '8px 16px', background: '#2e2d29', color: '#f5f0e8', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0 },
  btnRescan: { padding: '8px 16px', background: 'transparent', color: '#8a7e70', border: '1px solid #d0c4b0', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer', flexShrink: 0 },
  btnPrimary: { padding: '14px', background: '#2e2d29', color: '#f5f0e8', border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 700, cursor: 'pointer' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
  scanCard: { background: '#faf7f2', borderRadius: 16, padding: 24, width: '100%', maxWidth: 400 },
  scanTitle: { fontSize: 18, fontWeight: 700, color: '#1a1916', marginBottom: 16, textAlign: 'center' },
  videoContainer: { position: 'relative', borderRadius: 12, overflow: 'hidden', background: '#000', marginBottom: 12 },
  video: { width: '100%', height: 200, objectFit: 'cover' },
  scanFrame: { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 200, height: 80, border: '2px solid #3d6e4e', borderRadius: 8, opacity: 0.7 },
  scanHint: { textAlign: 'center', fontSize: 13, color: '#8a7e70', marginBottom: 12 },
  barcodeResult: { background: '#e8dfd0', borderRadius: 10, padding: 16, textAlign: 'center', marginBottom: 16 },
  barcodeLabel: { fontSize: 12, color: '#8a7e70', margin: '0 0 4px' },
  barcodeValue: { fontSize: 22, fontWeight: 700, color: '#1a1916', fontFamily: 'monospace', margin: 0 },
  btnRow: { display: 'flex', gap: 10 },
  btnCancel: { flex: 1, padding: '12px', background: 'transparent', color: '#3F3E39', border: '1px solid #d0c4b0', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnConfirm: { flex: 1, padding: '12px', background: '#3d6e4e', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  empty: { textAlign: 'center', padding: 40, color: '#8a7e70', fontSize: 14 },
};
